import React, { useEffect, useState } from "react";
import CertificateSetupPanel from "../components/certificates/CertificateSetupPanel";
import ConfigListManagementView from "./ConfigListManagementView";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import ListAltIcon from "@mui/icons-material/ListAlt";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import InfoIcon from "@mui/icons-material/Info";
import LockIcon from "@mui/icons-material/Lock";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PeopleIcon from "@mui/icons-material/People";
import PhoneIcon from "@mui/icons-material/Phone";
import SaveIcon from "@mui/icons-material/Save";
import SchoolIcon from "@mui/icons-material/School";
import ShieldIcon from "@mui/icons-material/Shield";
import WarningIcon from "@mui/icons-material/Warning";
import { useTenant } from "../contexts/TenantContext";
import { useActiveTenant } from "../hooks/useActiveTenant";
import NoTenantSelected from "../components/NoTenantSelected";
import { useTheme, alpha, darken } from "@mui/material/styles";
import {
  getTenantConfig,
  updateTenantConfig,
} from "../api/apiClient";

// ── Design tokens ─────────────────────────────────────────────────────────────
const DS = {
  ink: "var(--color-ink)",
  ink2: "var(--color-ink-2)",
  ink3: "var(--color-ink-3)",
  ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)",
  line2: "var(--color-surface-press-light)",
  line3: "var(--color-surface-press-light)",
  surface: "#FFFFFF",
  surface2: "var(--color-surface-2)",
  surface3: "var(--color-surface-press-light)",
  danger: "#B42318",
  danger50: "rgba(180,35,24,.08)",
  danger100: "rgba(180,35,24,.18)",
  live: "#C2410C",
  live50: "rgba(194,65,12,.10)",
  blue: "var(--color-primary)",
  blue50: "rgba(37,99,235,.10)",
  blue100: "rgba(37,99,235,.18)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
};

function makeThemeColors(theme) {
  const p = theme.palette.primary.main;
  const s = theme.palette.secondary.main;
  const a = theme.palette.warning.main;
  return {
    primary: p,
    primary700: darken(p, 0.3),
    primary10: alpha(p, 0.06),
    primary50: alpha(p, 0.1),
    green: s,
    green50: alpha(s, 0.1),
    green100: alpha(s, 0.18),
    amber: a,
    amber50: alpha(a, 0.1),
    amber100: alpha(a, 0.2),
  };
}

function makeCtrlSx(tPrimary) {
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      bgcolor: DS.surface2,
      "& fieldset": { borderWidth: "1.5px", borderColor: DS.line },
      "&:hover fieldset": { borderColor: "var(--color-hairline-cool)" },
      "&.Mui-focused fieldset": { borderColor: tPrimary, borderWidth: "1.5px" },
    },
    "& .MuiInputBase-input": { fontSize: 14, fontWeight: 500, color: DS.ink },
    "& .MuiInputAdornment-root": { color: DS.ink4 },
  };
}

const NAV_GROUPS = [
  {
    label: "General",
    items: [
      { key: "center", label: "Center Info", Icon: HomeIcon },
      { key: "staff", label: "Staff Directory", Icon: PeopleIcon },
      { key: "courses", label: "Courses", Icon: SchoolIcon },
      { key: "certificates", label: "Certificates", Icon: WorkspacePremiumIcon },
      { key: "dropdownLists", label: "Dropdown Lists", Icon: ListAltIcon },
    ],
  },
  {
    label: "Access & Comms",
    items: [
      { key: "portal", label: "Portal Access", Icon: LockIcon },
      { key: "email", label: "Notifications", Icon: NotificationsIcon },
      { key: "templates", label: "Email Settings", Icon: DescriptionIcon },
    ],
  },
  {
    label: "Legal",
    items: [{ key: "terms", label: "Terms & Policies", Icon: DescriptionIcon }],
  },
];

// ── Sub-components ─────────────────────────────────────────────────────────────

function Panel({
  children,
  icon,
  iconColor = "blue",
  title,
  subtitle,
  action,
}) {
  const TC = makeThemeColors(useTheme());
  const iconBg = {
    blue: TC.primary10,
    green: TC.green50,
    amber: TC.amber50,
    danger: DS.danger50,
    live: DS.live50,
    ink: DS.surface3,
  }[iconColor];
  const iconFg = {
    blue: TC.primary,
    green: TC.green,
    amber: TC.amber,
    danger: DS.danger,
    live: DS.live,
    ink: DS.ink3,
  }[iconColor];
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: `1px solid ${DS.line}`,
        borderRadius: "18px",
        boxShadow: DS.shadowSm,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: "18px 24px 16px",
          borderBottom: `1px solid ${DS.line2}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "13px" }}>
          {icon && (
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "10px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: iconBg,
                color: iconFg,
              }}
            >
              {icon}
            </Box>
          )}
          <Box>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                letterSpacing: "-.01em",
                color: DS.ink,
                fontFamily: "var(--font-display)",
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                sx={{ fontSize: "12.5px", color: DS.ink3, mt: "2px" }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {action}
      </Box>
      {children}
    </Box>
  );
}

function PanelBody({ children, gap = "18px" }) {
  return (
    <Box sx={{ p: "22px 24px", display: "flex", flexDirection: "column", gap }}>
      {children}
    </Box>
  );
}

function PanelFoot({ onSave, label }) {
  const TC = makeThemeColors(useTheme());
  const [saving, setSaving] = React.useState(false);
  const [status, setStatus] = React.useState(null);
  const handleSave = () => {
    setSaving(true);
    setStatus(null);
    onSave()
      .then(
        () => {
          setStatus("saved");
          setTimeout(() => setStatus((s) => (s === "saved" ? null : s)), 3000);
        },
        () => setStatus("error"),
      )
      .finally(() => setSaving(false));
  };
  const failed = status === "error";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: "14px 24px",
        borderTop: `1px solid ${DS.line2}`,
        bgcolor: DS.surface2,
      }}
    >
      <Box
        role="status"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "12.5px",
          fontWeight: 600,
          color: failed ? DS.danger : TC.green,
          opacity: status ? 1 : 0,
          transition: "opacity .3s",
        }}
      >
        {failed ? (
          <WarningIcon sx={{ fontSize: 14 }} />
        ) : (
          <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
        )}
        {failed ? "Failed to save. Please try again." : status === "saved" ? "Saved" : ""}
      </Box>
      <Button
        onClick={handleSave}
        disabled={saving}
        startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
        sx={{
          height: 38,
          px: "18px",
          borderRadius: "11px",
          bgcolor: TC.primary,
          color: "#fff",
          textTransform: "none",
          fontWeight: 700,
          fontSize: 13,
          boxShadow: `0 4px 12px -4px ${alpha(TC.primary, 0.45)}`,
          "&:hover": { bgcolor: TC.primary700 },
          "&.Mui-disabled": {
            bgcolor: TC.primary,
            opacity: 0.55,
            color: "#fff",
          },
        }}
      >
        {saving ? "Saving…" : label}
      </Button>
    </Box>
  );
}

function FieldLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: "11.5px",
        fontWeight: 700,
        letterSpacing: ".07em",
        textTransform: "uppercase",
        color: DS.ink3,
      }}
    >
      {children}
    </Typography>
  );
}

function FieldHint({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 12,
        color: DS.ink3,
        display: "flex",
        alignItems: "center",
        gap: "5px",
      }}
    >
      {children}
    </Typography>
  );
}

function ToggleRow({
  title,
  subtitle,
  checked,
  onChange,
  first = false,
  last = false,
}) {
  const TC = makeThemeColors(useTheme());
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "20px",
        py: "14px",
        borderBottom: last ? 0 : `1px solid ${DS.line2}`,
        pt: first ? 0 : "14px",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: DS.ink }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: "12.5px", color: DS.ink3, mt: "2px" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Switch
        checked={checked}
        onChange={onChange}
        inputProps={{ "aria-label": title }}
        sx={{
          "& .MuiSwitch-switchBase.Mui-checked": { color: TC.primary },
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            bgcolor: TC.primary,
          },
        }}
      />
    </Box>
  );
}

function Callout({ children, tone = "blue" }) {
  const TC = makeThemeColors(useTheme());
  const cfg = {
    blue: { bg: TC.primary10, border: alpha(TC.primary, 0.12), icon: TC.primary },
    amber: { bg: TC.amber50, border: TC.amber100, icon: TC.amber },
    green: { bg: TC.green50, border: TC.green100, icon: TC.green },
  }[tone];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "11px",
        borderRadius: "12px",
        p: "13px 15px",
        fontSize: 13,
        lineHeight: 1.6,
        bgcolor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        color: DS.ink2,
      }}
    >
      <InfoIcon
        sx={{ fontSize: 16, color: cfg.icon, flexShrink: 0, mt: "1px" }}
      />
      <Box>{children}</Box>
    </Box>
  );
}

// ── Person dialog ──────────────────────────────────────────────────────────────

function PersonDialog({ open, initial, onSave, onClose }) {
  const TC = makeThemeColors(useTheme());
  const empty = { name: "", title: "", email: "", phone: "" };
  const [form, setForm] = useState(empty);

  useEffect(() => {
    setForm(initial ?? empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));
  const valid = form.name.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1, fontFamily: "var(--font-display)" }}>
        {initial ? "Edit Person" : "Add Person"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Name"
            fullWidth
            size="small"
            value={form.name}
            onChange={set("name")}
            autoFocus
            required
          />
          <TextField
            label="Title / Role"
            fullWidth
            size="small"
            value={form.title}
            onChange={set("title")}
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            size="small"
            value={form.email}
            onChange={set("email")}
          />
          <TextField
            label="Phone"
            fullWidth
            size="small"
            value={form.phone}
            onChange={set("phone")}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            borderColor: DS.line,
            color: DS.ink2,
            fontWeight: 600,
            borderRadius: "10px",
            textTransform: "none",
            "&:hover": { borderColor: DS.ink4, bgcolor: DS.surface3 },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => valid && onSave(form)}
          disabled={!valid}
          sx={{
            bgcolor: TC.primary,
            color: "#fff",
            fontWeight: 700,
            borderRadius: "10px",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { bgcolor: TC.primary700 },
          }}
        >
          {initial ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function TrainingCenterPage() {
  const TC = makeThemeColors(useTheme());
  const { user } = useTenant();
  const navigate = useNavigate();
  const ctrlSx = makeCtrlSx(TC.primary);

  const [activeTab, setActiveTab] = useState("center");
  const [loading, setLoading] = useState(true);
  const { tenantId: selectedTenantId } = useActiveTenant();

  // Contact info state
  const [centerEmail, setCenterEmail] = useState("");
  const [centerPhone, setCenterPhone] = useState("");
  const [centerAddlPhone, setCenterAddlPhone] = useState("");
  const [centerAddress, setCenterAddress] = useState("");
  const [centerDirections, setCenterDirections] = useState("");
  const [centerSummary, setCenterSummary] = useState("");

  // Public safety state
  const [psPhysical, setPsPhysical] = useState("");
  const [psMailing, setPsMailing] = useState("");
  const [psPhone, setPsPhone] = useState("");
  const [psFax, setPsFax] = useState("");

  // Directory state
  const [directory, setDirectory] = useState([]);
  const [personDialog, setPersonDialog] = useState({
    open: false,
    idx: null,
    data: null,
  });

  // Terms state
  const [termsText, setTermsText] = useState("");

  // Courses state
  const [defaultCapacity, setDefaultCapacity] = useState(24);
  const [enableWaitlists, setEnableWaitlists] = useState(true);
  const [autoApprove, setAutoApprove] = useState(false);
  const [requirePrereqs, setRequirePrereqs] = useState(true);

  // Portal state
  const [portalPublicCatalog, setPortalPublicCatalog] = useState(true);
  const [portalGuestRegistration, setPortalGuestRegistration] = useState(true);
  const [portalShowEnrollment, setPortalShowEnrollment] = useState(true);

  // Email & Notifications state
  const [notifStudentRegistration, setNotifStudentRegistration] =
    useState(true);
  const [notifStudentReminder, setNotifStudentReminder] = useState(true);
  const [notifCertExpiry60, setNotifCertExpiry60] = useState(true);
  const [notifCertExpiry30, setNotifCertExpiry30] = useState(true);
  const [notifCertExpiry7, setNotifCertExpiry7] = useState(true);
  const [notifWaitlist, setNotifWaitlist] = useState(true);
  const [notifProposalDecision, setNotifProposalDecision] = useState(true);
  const [notifNewStudent, setNotifNewStudent] = useState(false);
  const [notifNewProposal, setNotifNewProposal] = useState(true);
  const [notifInstructorSignup, setNotifInstructorSignup] = useState(true);
  const [notifWeeklySummary, setNotifWeeklySummary] = useState(true);
  const [emailFromName, setEmailFromName] = useState("");
  const [emailReplyTo, setEmailReplyTo] = useState("");
  const [emailFooter, setEmailFooter] = useState("");
  // Extended Terms state
  const [cancellationWindow, setCancellationWindow] = useState("24h");
  const [cancellationFee, setCancellationFee] = useState(0);
  const [cancellationStatement, setCancellationStatement] = useState(
    "Students may cancel enrollment up to 24 hours before the class start time at no charge.",
  );
  const [requireCancellationAck, setRequireCancellationAck] = useState(true);
  const [waiverText, setWaiverText] = useState("");
  const [requireDigitalSig, setRequireDigitalSig] = useState(true);
  const [waiverApplyAll, setWaiverApplyAll] = useState(true);

  const canManage = user?.roles?.some((r) =>
    ["COURSE_COORDINATOR", "ADMIN", "GLOBAL_ADMIN"].includes(r),
  );

  useEffect(() => {
    if (!selectedTenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getTenantConfig(selectedTenantId)
      .then(populateFromConfig)
      .finally(() => setLoading(false));
  }, [selectedTenantId]);

  function populateFromConfig(cfg) {
    const tc = cfg?.trainingCenter ?? {};
    const c = tc.center ?? {};
    const ps = tc.publicSafety ?? {};
    setCenterEmail(c.email ?? "");
    setCenterPhone(c.phone ?? "");
    setCenterAddlPhone(c.additionalPhone ?? "");
    setCenterAddress(c.physicalAddress ?? "");
    setCenterDirections(c.directions ?? "");
    setCenterSummary(c.summary ?? "");
    setPsPhysical(ps.physicalAddress ?? "");
    setPsMailing(ps.mailingAddress ?? "");
    setPsPhone(ps.phone ?? "");
    setPsFax(ps.fax ?? "");
    setDirectory(tc.directory ?? []);
    setTermsText(cfg?.genericTermsConditions ?? "");
    const enroll = tc.enrollment ?? {};
    setDefaultCapacity(enroll.defaultCapacity ?? 24);
    setEnableWaitlists(enroll.enableWaitlists ?? true);
    setAutoApprove(enroll.autoApprove ?? false);
    setRequirePrereqs(enroll.requirePrereqs ?? true);
    const portal = tc.portal ?? {};
    setPortalPublicCatalog(portal.publicCatalog ?? true);
    setPortalGuestRegistration(portal.guestRegistration ?? true);
    setPortalShowEnrollment(portal.showEnrollmentNumbers ?? true);
    const notif = tc.notifications ?? {};
    setNotifStudentRegistration(notif.studentRegistration ?? true);
    setNotifStudentReminder(notif.studentReminder ?? true);
    setNotifCertExpiry60(notif.certExpiry60 ?? true);
    setNotifCertExpiry30(notif.certExpiry30 ?? true);
    setNotifCertExpiry7(notif.certExpiry7 ?? true);
    setNotifWaitlist(notif.waitlist ?? true);
    setNotifProposalDecision(notif.proposalDecision ?? true);
    setNotifNewStudent(notif.newStudent ?? false);
    setNotifNewProposal(notif.newProposal ?? true);
    setNotifInstructorSignup(notif.instructorSignup ?? true);
    setNotifWeeklySummary(notif.weeklySummary ?? true);
    const sender = tc.emailSender ?? {};
    setEmailFromName(sender.fromName ?? "");
    setEmailReplyTo(sender.replyTo ?? "");
    setEmailFooter(sender.footer ?? "");
    const cancel = tc.cancellationPolicy ?? {};
    setCancellationWindow(cancel.window ?? "24h");
    setCancellationFee(cancel.fee ?? 0);
    setCancellationStatement(
      cancel.statement ??
        "Students may cancel enrollment up to 24 hours before the class start time at no charge.",
    );
    setRequireCancellationAck(cancel.requireAcknowledgment ?? true);
    const waiver = tc.liabilityWaiver ?? {};
    setWaiverText(waiver.text ?? "");
    setRequireDigitalSig(waiver.requireDigitalSignature ?? true);
    setWaiverApplyAll(waiver.applyToAllPhysical ?? true);
  }

  // The backend deep-merges each update, so a save sends only its own section.
  const saveTrainingCenter = (section) =>
    updateTenantConfig(selectedTenantId, { trainingCenter: section });

  const saveCenter = () =>
    saveTrainingCenter({
      center: {
        email: centerEmail,
        phone: centerPhone,
        additionalPhone: centerAddlPhone,
        physicalAddress: centerAddress,
        directions: centerDirections,
        summary: centerSummary,
      },
    });

  const savePublicSafety = () =>
    saveTrainingCenter({
      publicSafety: {
        physicalAddress: psPhysical,
        mailingAddress: psMailing,
        phone: psPhone,
        fax: psFax,
      },
    });

  const saveDirectory = () => saveTrainingCenter({ directory });

  const saveEnrollment = () =>
    saveTrainingCenter({
      enrollment: {
        defaultCapacity,
        enableWaitlists,
        autoApprove,
        requirePrereqs,
      },
    });

  const savePortal = () =>
    saveTrainingCenter({
      portal: {
        publicCatalog: portalPublicCatalog,
        guestRegistration: portalGuestRegistration,
        showEnrollmentNumbers: portalShowEnrollment,
      },
    });

  const saveNotifications = () =>
    saveTrainingCenter({
      notifications: {
        studentRegistration: notifStudentRegistration,
        studentReminder: notifStudentReminder,
        certExpiry60: notifCertExpiry60,
        certExpiry30: notifCertExpiry30,
        certExpiry7: notifCertExpiry7,
        waitlist: notifWaitlist,
        proposalDecision: notifProposalDecision,
        newStudent: notifNewStudent,
        newProposal: notifNewProposal,
        instructorSignup: notifInstructorSignup,
        weeklySummary: notifWeeklySummary,
      },
    });

  const saveSender = () =>
    saveTrainingCenter({
      emailSender: {
        fromName: emailFromName,
        replyTo: emailReplyTo,
        footer: emailFooter,
      },
    });

  const saveTerms = () =>
    updateTenantConfig(selectedTenantId, { genericTermsConditions: termsText });

  const saveCancellation = () =>
    saveTrainingCenter({
      cancellationPolicy: {
        window: cancellationWindow,
        fee: cancellationFee,
        statement: cancellationStatement,
        requireAcknowledgment: requireCancellationAck,
      },
    });

  const saveWaiver = () =>
    saveTrainingCenter({
      liabilityWaiver: {
        text: waiverText,
        requireDigitalSignature: requireDigitalSig,
        applyToAllPhysical: waiverApplyAll,
      },
    });

  const openAddPerson = () =>
    setPersonDialog({ open: true, idx: null, data: null });

  const openEditPerson = (idx) =>
    setPersonDialog({ open: true, idx, data: { ...directory[idx] } });

  const handlePersonSave = (form) => {
    if (personDialog.idx === null) {
      setDirectory((d) => [...d, form]);
    } else {
      setDirectory((d) => d.map((p, i) => (i === personDialog.idx ? form : p)));
    }
    setPersonDialog({ open: false, idx: null, data: null });
  };

  const removePerson = (idx) =>
    setDirectory((d) => d.filter((_, i) => i !== idx));

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canManage) {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto", mt: 4 }}>
        <Alert severity="error">
          You do not have permission to manage training center settings.
        </Alert>
      </Box>
    );
  }

  if (!selectedTenantId) {
    return (
      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        <NoTenantSelected message="Select a training center from the top bar to manage its settings." />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto" }}>
      {/* Page header */}
      <Box sx={{ mb: "28px" }}>
        <Typography
          sx={{
            fontSize: 26,
            fontWeight: 800,
            letterSpacing: "-.03em",
            color: DS.ink,
            mb: "5px",
            fontFamily: "var(--font-display)",
          }}
        >
          Training Center
        </Typography>
        <Typography sx={{ fontSize: 14, color: DS.ink3 }}>
          Manage settings, staff, courses, and policies for this training center
        </Typography>
      </Box>

      {/* Global admin tenant selector lives in the header (Layout.js) */}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "210px 1fr" }, gap: "24px", alignItems: "start" }}>

        {/* ── Sidebar nav ── */}
        <Box
          sx={{
            position: { md: "sticky" },
            top: { md: "76px" },
            display: "flex",
            flexDirection: "column",
            gap: "2px",
          }}
        >
          {NAV_GROUPS.map((group, gi) => (
            <React.Fragment key={group.label}>
              {gi > 0 && <Box sx={{ height: 1, bgcolor: DS.line2, m: "5px 10px" }} />}
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: ".1em",
                  textTransform: "uppercase",
                  color: DS.ink4,
                  p: "12px 10px 5px",
                }}
              >
                {group.label}
              </Typography>
              {group.items.map(({ key, label, Icon }) => (
                <Box
                  key={key}
                  onClick={() => setActiveTab(key)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    p: "9px 12px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    bgcolor: activeTab === key ? TC.primary10 : "transparent",
                    color: activeTab === key ? TC.primary : DS.ink3,
                    fontWeight: 600,
                    fontSize: "13.5px",
                    transition: "all .12s",
                    "&:hover": {
                      bgcolor: activeTab === key ? TC.primary10 : DS.surface3,
                      color: activeTab === key ? TC.primary : DS.ink,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: "7px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "inherit",
                    }}
                  >
                    <Icon sx={{ fontSize: 15 }} />
                  </Box>
                  {label}
                </Box>
              ))}
            </React.Fragment>
          ))}
        </Box>

        {/* ── Main content ── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            minWidth: 0,
          }}
        >
          {/* ══ CERTIFICATES ══ */}
          {activeTab === "certificates" && (
            <CertificateSetupPanel key={selectedTenantId}
              canEdit={!!user?.roles?.some((r) => ["ADMIN", "GLOBAL_ADMIN"].includes(r))} />
          )}

          {/* ══ DROPDOWN LISTS ══ */}
          {activeTab === "dropdownLists" && <ConfigListManagementView />}

          {/* ══ CENTER INFO ══ */}
          {activeTab === "center" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: "-.025em",
                    color: DS.ink,
                    mb: "4px",
                    fontFamily: "var(--font-display)",
                  }}
                >
                  Center Info
                </Typography>
                <Typography sx={{ fontSize: 14, color: DS.ink3 }}>
                  Manage public-facing contact details for this training center
                </Typography>
              </Box>

              <Panel
                icon={<PhoneIcon sx={{ fontSize: 17 }} />}
                iconColor="blue"
                title="Training Center Contact"
                subtitle="Shown to students and instructors across the platform"
              >
                <PanelBody>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: "14px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "7px",
                      }}
                    >
                      <FieldLabel>Email Address</FieldLabel>
                      <TextField
                        size="small"
                        fullWidth
                        type="email"
                        placeholder="info@trainingcenter.com"
                        value={centerEmail}
                        onChange={(e) => setCenterEmail(e.target.value)}
                        sx={ctrlSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon
                                sx={{ fontSize: 15, color: DS.ink4 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "7px",
                      }}
                    >
                      <FieldLabel>Primary Phone</FieldLabel>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="(555) 000-0000"
                        value={centerPhone}
                        onChange={(e) => setCenterPhone(e.target.value)}
                        sx={ctrlSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon
                                sx={{ fontSize: 15, color: DS.ink4 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: "14px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "7px",
                      }}
                    >
                      <FieldLabel>Additional Phone</FieldLabel>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="(555) 000-0000"
                        value={centerAddlPhone}
                        onChange={(e) => setCenterAddlPhone(e.target.value)}
                        sx={ctrlSx}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "7px",
                      }}
                    >
                      <FieldLabel>Physical Address</FieldLabel>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="456 Training Rd, City, State"
                        value={centerAddress}
                        onChange={(e) => setCenterAddress(e.target.value)}
                        sx={ctrlSx}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "7px",
                    }}
                  >
                    <FieldLabel>Directions</FieldLabel>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder="Take exit 12 off Highway 1..."
                      value={centerDirections}
                      onChange={(e) => setCenterDirections(e.target.value)}
                      sx={ctrlSx}
                    />
                    <FieldHint>Shown on registration confirmations</FieldHint>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "7px",
                    }}
                  >
                    <FieldLabel>Summary</FieldLabel>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={3}
                      data-testid="center-summary"
                      placeholder="A short description of your training center..."
                      value={centerSummary}
                      onChange={(e) => setCenterSummary(e.target.value)}
                      inputProps={{ maxLength: 500 }}
                      sx={ctrlSx}
                    />
                    <FieldHint>
                      Shown on the public home page. {centerSummary.length}/500
                    </FieldHint>
                  </Box>
                </PanelBody>
                <PanelFoot
                  onSave={saveCenter}
                  label="Save Contact Info"
                />
              </Panel>

              <Panel
                icon={<ShieldIcon sx={{ fontSize: 17 }} />}
                iconColor="green"
                title="Public Safety Contact"
                subtitle="Emergency and public-facing address for the affiliated agency"
              >
                <PanelBody>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: "14px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "7px",
                      }}
                    >
                      <FieldLabel>Physical Address</FieldLabel>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="123 Main Street"
                        value={psPhysical}
                        onChange={(e) => setPsPhysical(e.target.value)}
                        sx={ctrlSx}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "7px",
                      }}
                    >
                      <FieldLabel>Mailing Address</FieldLabel>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="PO Box 123"
                        value={psMailing}
                        onChange={(e) => setPsMailing(e.target.value)}
                        sx={ctrlSx}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: "14px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "7px",
                      }}
                    >
                      <FieldLabel>Phone</FieldLabel>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="(555) 000-0000"
                        value={psPhone}
                        onChange={(e) => setPsPhone(e.target.value)}
                        sx={ctrlSx}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "7px",
                      }}
                    >
                      <FieldLabel>Fax</FieldLabel>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="(555) 000-0000"
                        value={psFax}
                        onChange={(e) => setPsFax(e.target.value)}
                        sx={ctrlSx}
                      />
                    </Box>
                  </Box>
                </PanelBody>
                <PanelFoot
                  onSave={savePublicSafety}
                  label="Save Public Safety"
                />
              </Panel>
            </Box>
          )}

          {/* ══ STAFF DIRECTORY ══ */}
          {activeTab === "staff" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: "-.025em",
                    color: DS.ink,
                    mb: "4px",
                    fontFamily: "var(--font-display)",
                  }}
                >
                  Staff Directory
                </Typography>
                <Typography sx={{ fontSize: 14, color: DS.ink3 }}>
                  Public-facing contacts displayed on the training center
                  profile page
                </Typography>
              </Box>

              <Panel
                icon={<PeopleIcon sx={{ fontSize: 17 }} />}
                iconColor="amber"
                title="Staff Members"
                subtitle={`${directory.length} person${directory.length !== 1 ? "s" : ""} listed`}
                action={
                  <Button
                    onClick={openAddPerson}
                    startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                    sx={{
                      height: 34,
                      px: "14px",
                      borderRadius: "9px",
                      border: `1.5px solid ${DS.line}`,
                      bgcolor: DS.surface,
                      color: DS.ink2,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "13px",
                      "&:hover": { bgcolor: DS.surface3, color: DS.ink },
                    }}
                  >
                    Add Person
                  </Button>
                }
              >
                <PanelBody gap="0">
                  {directory.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <PeopleIcon
                        sx={{ fontSize: 36, color: DS.line, mb: 1 }}
                      />
                      <Typography sx={{ fontSize: 14, color: DS.ink3 }}>
                        No staff members yet
                      </Typography>
                    </Box>
                  ) : (
                    directory.map((person, idx) => {
                      const initials = (person.name || "?")
                        .split(" ")
                        .map((w) => w[0])
                        .join("")
                        .slice(0, 2)
                        .toUpperCase();
                      const avatarColors = [
                        TC.primary,
                        "var(--color-primary)",
                        TC.green,
                        TC.amber,
                        DS.ink3,
                      ];
                      const avatarBg = avatarColors[idx % avatarColors.length];
                      return (
                        <Box
                          key={idx}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "14px",
                            py: "14px",
                            borderBottom:
                              idx < directory.length - 1
                                ? `1px solid ${DS.line2}`
                                : 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: avatarBg,
                              color: "#fff",
                              fontSize: 14,
                              fontWeight: 800,
                            }}
                          >
                            {initials}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: "14.5px",
                                fontWeight: 700,
                                color: DS.ink,
                              }}
                            >
                              {person.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12.5px",
                                color: DS.ink3,
                                mt: "1px",
                              }}
                            >
                              {person.title || "—"}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "16px",
                              flexWrap: "wrap",
                            }}
                          >
                            {person.email && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  fontSize: 13,
                                  color: DS.ink2,
                                  fontWeight: 500,
                                }}
                              >
                                <EmailIcon
                                  sx={{ fontSize: 13, color: DS.ink4 }}
                                />
                                {person.email}
                              </Box>
                            )}
                            {person.phone && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  fontSize: 13,
                                  color: DS.ink2,
                                  fontWeight: 500,
                                }}
                              >
                                <PhoneIcon
                                  sx={{ fontSize: 13, color: DS.ink4 }}
                                />
                                {person.phone}
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ display: "flex", gap: "6px" }}>
                            <IconButton
                              size="small"
                              onClick={() => openEditPerson(idx)}
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "8px",
                                border: `1.5px solid ${DS.line}`,
                                bgcolor: "#fff",
                                color: DS.ink3,
                                "&:hover": {
                                  bgcolor: DS.surface3,
                                  color: DS.ink,
                                  borderColor: "var(--color-hairline-cool)",
                                },
                              }}
                            >
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => removePerson(idx)}
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "8px",
                                border: `1.5px solid ${DS.line}`,
                                bgcolor: "#fff",
                                color: DS.ink3,
                                "&:hover": {
                                  bgcolor: DS.danger50,
                                  color: DS.danger,
                                  borderColor: DS.danger100,
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </PanelBody>
                <PanelFoot
                  onSave={saveDirectory}
                  label="Save Directory"
                />
              </Panel>
            </Box>
          )}

          {/* ══ COURSES ══ */}
          {activeTab === "courses" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: "-.025em",
                    color: DS.ink,
                    mb: "4px",
                    fontFamily: "var(--font-display)",
                  }}
                >
                  Courses
                </Typography>
                <Typography sx={{ fontSize: 14, color: DS.ink3 }}>
                  Enrollment defaults applied to new classes
                </Typography>
              </Box>

              {/* Enrollment Settings */}
              <Panel
                icon={<PeopleIcon sx={{ fontSize: 17 }} />}
                iconColor="amber"
                title="Enrollment Settings"
                subtitle="Default rules applied to all new courses unless overridden"
              >
                <PanelBody>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "7px",
                    }}
                  >
                    <FieldLabel>Default Class Capacity</FieldLabel>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          border: `1.5px solid ${DS.line}`,
                          borderRadius: "12px",
                          overflow: "hidden",
                          bgcolor: DS.surface2,
                          width: 160,
                        }}
                      >
                        <Box
                          component="button"
                          type="button"
                          onClick={() =>
                            setDefaultCapacity((v) => Math.max(1, v - 5))
                          }
                          sx={{
                            width: 44,
                            height: 44,
                            border: 0,
                            bgcolor: "transparent",
                            color: DS.ink3,
                            fontSize: 20,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "&:hover": { bgcolor: DS.surface3, color: DS.ink },
                            fontFamily: "inherit",
                          }}
                        >
                          −
                        </Box>
                        <Box
                          sx={{
                            width: "1px",
                            bgcolor: DS.line2,
                            height: 26,
                            flexShrink: 0,
                          }}
                        />
                        <Box
                          component="input"
                          type="number"
                          value={defaultCapacity}
                          onChange={(e) =>
                            setDefaultCapacity(
                              Math.max(
                                1,
                                Math.min(
                                  200,
                                  parseInt(e.target.value, 10) || 1,
                                ),
                              ),
                            )
                          }
                          sx={{
                            flex: 1,
                            textAlign: "center",
                            fontSize: 16,
                            fontWeight: 800,
                            color: DS.ink,
                            letterSpacing: "-.02em",
                            border: 0,
                            bgcolor: "transparent",
                            outline: "none",
                            fontFamily: "inherit",
                            height: 44,
                            "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                              { WebkitAppearance: "none" },
                          }}
                        />
                        <Box
                          sx={{
                            width: "1px",
                            bgcolor: DS.line2,
                            height: 26,
                            flexShrink: 0,
                          }}
                        />
                        <Box
                          component="button"
                          type="button"
                          onClick={() =>
                            setDefaultCapacity((v) => Math.min(200, v + 5))
                          }
                          sx={{
                            width: 44,
                            height: 44,
                            border: 0,
                            bgcolor: "transparent",
                            color: DS.ink3,
                            fontSize: 20,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "&:hover": { bgcolor: DS.surface3, color: DS.ink },
                            fontFamily: "inherit",
                          }}
                        >
                          +
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: 13, color: DS.ink3 }}>
                        students per class
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <ToggleRow
                      title="Enable Waitlists"
                      subtitle="When a class is full, students can join a waitlist and get notified of openings"
                      checked={enableWaitlists}
                      onChange={(e) => setEnableWaitlists(e.target.checked)}
                      first
                    />
                    <ToggleRow
                      title="Auto-Approve Registrations"
                      subtitle="Registrations are confirmed immediately without coordinator review"
                      checked={autoApprove}
                      onChange={(e) => setAutoApprove(e.target.checked)}
                    />
                    <ToggleRow
                      title="Require Prerequisite Verification"
                      subtitle="Students must have completed prerequisite courses before registering"
                      checked={requirePrereqs}
                      onChange={(e) => setRequirePrereqs(e.target.checked)}
                      last
                    />
                  </Box>
                </PanelBody>
                <PanelFoot
                  onSave={saveEnrollment}
                  label="Save Enrollment Settings"
                />
              </Panel>

            </Box>
          )}

          {/* ══ PORTAL ACCESS ══ */}
          {activeTab === "portal" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: "-.025em",
                    color: DS.ink,
                    mb: "4px",
                    fontFamily: "var(--font-display)",
                  }}
                >
                  Portal Access
                </Typography>
                <Typography sx={{ fontSize: 14, color: DS.ink3 }}>
                  Control what the public can see and do without an account
                </Typography>
              </Box>

              {/* Portal Access */}
              <Panel
                icon={<SchoolIcon sx={{ fontSize: 17 }} />}
                iconColor="green"
                title="Portal Access"
                subtitle="Control public visibility and self-service registration"
              >
                <PanelBody>
                  <Box>
                    <ToggleRow
                      title="Public Course Catalog"
                      subtitle="Anyone can browse available courses without signing in"
                      checked={portalPublicCatalog}
                      onChange={(e) => setPortalPublicCatalog(e.target.checked)}
                      first
                    />
                    <ToggleRow
                      title="Guest Registration"
                      subtitle="Non-enrolled users can create an account and register for open classes"
                      checked={portalGuestRegistration}
                      onChange={(e) =>
                        setPortalGuestRegistration(e.target.checked)
                      }
                    />
                    <ToggleRow
                      title="Show Enrollment Numbers"
                      subtitle="Display available seat counts publicly on the catalog"
                      checked={portalShowEnrollment}
                      onChange={(e) =>
                        setPortalShowEnrollment(e.target.checked)
                      }
                      last
                    />
                  </Box>
                </PanelBody>
                <PanelFoot
                  onSave={savePortal}
                  label="Save Portal Settings"
                />
              </Panel>
            </Box>
          )}

          {/* ══ EMAIL & NOTIFICATIONS ══ */}
          {activeTab === "email" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: "-.025em",
                    color: DS.ink,
                    mb: "4px",
                    fontFamily: "var(--font-display)",
                  }}
                >
                  Email &amp; Notifications
                </Typography>
                <Typography sx={{ fontSize: 14, color: DS.ink3 }}>
                  Configure automated email triggers and sender settings for
                  this center
                </Typography>
              </Box>

              {/* Notification Triggers */}
              <Panel
                icon={<NotificationsIcon sx={{ fontSize: 17 }} />}
                iconColor="blue"
                title="Notification Triggers"
                subtitle="Choose which events send automated emails"
              >
                <PanelBody>
                  <Typography
                    sx={{
                      fontSize: "10.5px",
                      fontWeight: 700,
                      letterSpacing: ".08em",
                      textTransform: "uppercase",
                      color: DS.ink4,
                      pb: "8px",
                    }}
                  >
                    Student Notifications
                  </Typography>
                  <Box>
                    <ToggleRow
                      title="Registration Confirmation"
                      subtitle="Sent when a student successfully registers for a class"
                      checked={notifStudentRegistration}
                      onChange={(e) =>
                        setNotifStudentRegistration(e.target.checked)
                      }
                      first
                    />
                    <ToggleRow
                      title="Session Reminder"
                      subtitle="Email the day before an upcoming class session"
                      checked={notifStudentReminder}
                      onChange={(e) =>
                        setNotifStudentReminder(e.target.checked)
                      }
                    />
                    <ToggleRow
                      title="Certification Expiry — 60 Days"
                      subtitle="First reminder before a certification lapses"
                      checked={notifCertExpiry60}
                      onChange={(e) => setNotifCertExpiry60(e.target.checked)}
                    />
                    <ToggleRow
                      title="Certification Expiry — 30 Days"
                      subtitle="Second reminder — more urgent"
                      checked={notifCertExpiry30}
                      onChange={(e) => setNotifCertExpiry30(e.target.checked)}
                    />
                    <ToggleRow
                      title="Certification Expiry — 7 Days"
                      subtitle="Final reminder before expiry"
                      checked={notifCertExpiry7}
                      onChange={(e) => setNotifCertExpiry7(e.target.checked)}
                    />
                    <ToggleRow
                      title="Waitlist Position Available"
                      subtitle="Notify student when a seat opens in a waitlisted class"
                      checked={notifWaitlist}
                      onChange={(e) => setNotifWaitlist(e.target.checked)}
                      last
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "10.5px",
                      fontWeight: 700,
                      letterSpacing: ".08em",
                      textTransform: "uppercase",
                      color: DS.ink4,
                      pt: "6px",
                      pb: "8px",
                    }}
                  >
                    Instructor Notifications
                  </Typography>
                  <Box>
                    <ToggleRow
                      title="Proposal Decision"
                      subtitle="Notify instructor when their course proposal, session request or slot sign-up is decided"
                      checked={notifProposalDecision}
                      onChange={(e) =>
                        setNotifProposalDecision(e.target.checked)
                      }
                      first
                    />
                    <ToggleRow
                      title="New Student Enrolled"
                      subtitle="Notify instructor when a student joins their class"
                      checked={notifNewStudent}
                      onChange={(e) => setNotifNewStudent(e.target.checked)}
                      last
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "10.5px",
                      fontWeight: 700,
                      letterSpacing: ".08em",
                      textTransform: "uppercase",
                      color: DS.ink4,
                      pt: "6px",
                      pb: "8px",
                    }}
                  >
                    Coordinator Notifications
                  </Typography>
                  <Box>
                    <ToggleRow
                      title="New Proposal Submitted"
                      subtitle="Alert coordinator when an instructor submits a course for review"
                      checked={notifNewProposal}
                      onChange={(e) => setNotifNewProposal(e.target.checked)}
                      first
                    />
                    <ToggleRow
                      title="Instructor Slot Sign-up"
                      subtitle="Alert coordinators when an instructor signs up for a session slot that needs approval"
                      checked={notifInstructorSignup}
                      onChange={(e) => setNotifInstructorSignup(e.target.checked)}
                    />
                    <ToggleRow
                      title="Weekly Activity Summary"
                      subtitle="Digest of enrollments, sign-offs, and expiring certs each Monday"
                      checked={notifWeeklySummary}
                      onChange={(e) => setNotifWeeklySummary(e.target.checked)}
                      last
                    />
                  </Box>
                </PanelBody>
                <PanelFoot
                  onSave={saveNotifications}
                  label="Save Notification Settings"
                />
              </Panel>
            </Box>
          )}

          {/* ══ EMAIL SETTINGS ══ */}
          {activeTab === "templates" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: "-.025em",
                    color: DS.ink,
                    mb: "4px",
                    fontFamily: "var(--font-display)",
                  }}
                >
                  Email Settings
                </Typography>
                <Typography sx={{ fontSize: 14, color: DS.ink3 }}>
                  Manage sender identity and customize automated email templates
                  for this center
                </Typography>
              </Box>

              {/* Email Sender Settings */}
              <Panel
                icon={<EmailIcon sx={{ fontSize: 17 }} />}
                iconColor="amber"
                title="Email Sender Settings"
                subtitle="How outgoing notifications appear to recipients"
              >
                <PanelBody>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: "14px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "7px",
                      }}
                    >
                      <FieldLabel>From Name</FieldLabel>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Training Center"
                        value={emailFromName}
                        onChange={(e) => setEmailFromName(e.target.value)}
                        sx={ctrlSx}
                      />
                      <FieldHint>
                        Appears as the sender name in student inboxes
                      </FieldHint>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "7px",
                      }}
                    >
                      <FieldLabel>Reply-To Email</FieldLabel>
                      <TextField
                        size="small"
                        fullWidth
                        type="email"
                        placeholder="info@trainingcenter.com"
                        value={emailReplyTo}
                        onChange={(e) => setEmailReplyTo(e.target.value)}
                        sx={ctrlSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon
                                sx={{ fontSize: 15, color: DS.ink4 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "7px",
                    }}
                  >
                    <FieldLabel>Email Footer Text</FieldLabel>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder="Training Center · 123 Main St · City, State 00000"
                      value={emailFooter}
                      onChange={(e) => setEmailFooter(e.target.value)}
                      sx={ctrlSx}
                    />
                    <FieldHint>
                      Appended to the bottom of every outgoing email
                    </FieldHint>
                  </Box>
                </PanelBody>
                <PanelFoot
                  onSave={saveSender}
                  label="Save Sender Settings"
                />
              </Panel>

              {/* Email Templates */}
              <Panel
                icon={<DescriptionIcon sx={{ fontSize: 17 }} />}
                iconColor="ink"
                title="Email Templates"
                subtitle="Every email has a built-in default. Customize the subject or intro of any of them in the Email Library."
                action={
                  <Button
                    size="small"
                    onClick={() => navigate("/email-library")}
                    sx={{ textTransform: "none", fontWeight: 600, fontSize: 12.5, color: DS.ink2 }}
                  >
                    Open Email Library
                  </Button>
                }
              >
                <PanelBody>
                  <Typography sx={{ fontSize: 13.5, color: DS.ink3 }}>
                    Templates you don&apos;t customize keep using the default wording. Security emails such as
                    password resets and sign-in links can&apos;t be changed.
                  </Typography>
                </PanelBody>
              </Panel>
            </Box>
          )}

          {/* ══ TERMS & POLICIES ══ */}
          {activeTab === "terms" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: "-.025em",
                    color: DS.ink,
                    mb: "4px",
                    fontFamily: "var(--font-display)",
                  }}
                >
                  Terms &amp; Policies
                </Typography>
                <Typography sx={{ fontSize: 14, color: DS.ink3 }}>
                  Legal documents shown to students during registration and
                  onboarding
                </Typography>
              </Box>

              {/* Default T&C */}
              <Panel
                icon={<DescriptionIcon sx={{ fontSize: 17 }} />}
                iconColor="danger"
                title="Default Terms & Conditions"
                subtitle="Shown to students at registration when no class-specific terms are set"
              >
                <PanelBody>
                  <Callout tone="blue">
                    Individual classes can override these terms on their{" "}
                    <strong>Registration</strong> tab. This text is used as the
                    fallback when no class-specific terms are configured.
                  </Callout>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "7px",
                    }}
                  >
                    <FieldLabel>Terms &amp; Conditions Text</FieldLabel>
                    <TextField
                      multiline
                      minRows={6}
                      fullWidth
                      size="small"
                      placeholder="Enter the default terms and conditions that students must accept before registering…"
                      value={termsText}
                      onChange={(e) => setTermsText(e.target.value)}
                      sx={ctrlSx}
                    />
                  </Box>
                </PanelBody>
                <PanelFoot
                  onSave={saveTerms}
                  label="Save Terms"
                />
              </Panel>

              {/* Cancellation Policy */}
              <Panel
                icon={<CancelIcon sx={{ fontSize: 17 }} />}
                iconColor="amber"
                title="Cancellation Policy"
                subtitle="Rules governing student withdrawals and refunds"
              >
                <PanelBody>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: "14px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "7px",
                      }}
                    >
                      <FieldLabel>Cancellation Window</FieldLabel>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={cancellationWindow}
                        onChange={(e) => setCancellationWindow(e.target.value)}
                        sx={ctrlSx}
                      >
                        <MenuItem value="none">
                          No cancellation allowed
                        </MenuItem>
                        <MenuItem value="24h">
                          Up to 24 hours before class
                        </MenuItem>
                        <MenuItem value="48h">
                          Up to 48 hours before class
                        </MenuItem>
                        <MenuItem value="72h">
                          Up to 72 hours before class
                        </MenuItem>
                        <MenuItem value="1w">
                          Up to 1 week before class
                        </MenuItem>
                      </TextField>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "7px",
                      }}
                    >
                      <FieldLabel>Late Cancellation Fee</FieldLabel>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        value={cancellationFee}
                        onChange={(e) =>
                          setCancellationFee(parseFloat(e.target.value) || 0)
                        }
                        sx={ctrlSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: DS.ink4,
                                }}
                              >
                                $
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FieldHint>Enter 0 for no fee</FieldHint>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "7px",
                    }}
                  >
                    <FieldLabel>Policy Statement</FieldLabel>
                    <TextField
                      multiline
                      minRows={4}
                      fullWidth
                      size="small"
                      placeholder="Describe your cancellation and refund policy in plain language…"
                      value={cancellationStatement}
                      onChange={(e) => setCancellationStatement(e.target.value)}
                      sx={ctrlSx}
                    />
                  </Box>
                  <Box>
                    <ToggleRow
                      title="Require Student Acknowledgment"
                      subtitle="Students must check a box confirming they've read the cancellation policy before registering"
                      checked={requireCancellationAck}
                      onChange={(e) =>
                        setRequireCancellationAck(e.target.checked)
                      }
                      first
                      last
                    />
                  </Box>
                </PanelBody>
                <PanelFoot
                  onSave={saveCancellation}
                  label="Save Cancellation Policy"
                />
              </Panel>

              {/* Liability Waiver */}
              <Panel
                icon={<WarningIcon sx={{ fontSize: 17 }} />}
                iconColor="live"
                title="Liability Waiver"
                subtitle="Physical safety waiver presented to students before hands-on or field training"
              >
                <PanelBody>
                  <Callout tone="amber">
                    This waiver is only shown for classes that have{" "}
                    <strong>Physical Training</strong> enabled. Consult your
                    legal team before modifying waiver language.
                  </Callout>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "7px",
                    }}
                  >
                    <FieldLabel>Waiver Text</FieldLabel>
                    <TextField
                      multiline
                      minRows={6}
                      fullWidth
                      size="small"
                      placeholder="Enter the full liability waiver text…"
                      value={waiverText}
                      onChange={(e) => setWaiverText(e.target.value)}
                      sx={ctrlSx}
                    />
                  </Box>
                  <Box>
                    <ToggleRow
                      title="Require Digital Signature"
                      subtitle="Student must type their full name to acknowledge the waiver"
                      checked={requireDigitalSig}
                      onChange={(e) => setRequireDigitalSig(e.target.checked)}
                      first
                    />
                    <ToggleRow
                      title="Apply to All Physical Classes"
                      subtitle="Show this waiver for every course with field or practical components"
                      checked={waiverApplyAll}
                      onChange={(e) => setWaiverApplyAll(e.target.checked)}
                      last
                    />
                  </Box>
                </PanelBody>
                <PanelFoot
                  onSave={saveWaiver}
                  label="Save Waiver"
                />
              </Panel>
            </Box>
          )}
        </Box>
      </Box>

      <PersonDialog
        open={personDialog.open}
        initial={personDialog.data}
        onSave={handlePersonSave}
        onClose={() => setPersonDialog({ open: false, idx: null, data: null })}
      />
    </Box>
  );
}
